import { FC, type JSX } from 'react';

import { FlowRadioGroup, getRadioGroupNameProps } from '@eneco/flows/components';
import { requiredRadioChoice } from '@eneco/flows/helpers';
import { RadioTile } from '@eneco/sparky';
import { OnePerson, TwoPeople, ThreePeople, FourPeople, FivePeople } from '@eneco/sparky/illustrations';
import { usePlaceholderContent } from '@sitecore/common';
import { ResidentsStepRendering } from '@sitecore/types/ResidentsStep';
import * as yup from 'yup';

import { StepShell } from '../../components/StepShell/StepShell';
import { useFlowStep } from '../../hooks';

interface FormFields {
  residents: string;
}

const generateInputElement = (value: string, label: string, description?: string): JSX.Element => {
  let icon: JSX.Element;

  switch (value) {
    case '1':
      icon = <OnePerson size="small" />;
      break;

    case '2':
      icon = <TwoPeople size="small" />;
      break;

    case '3':
      icon = <ThreePeople size="small" />;
      break;

    case '4':
      icon = <FourPeople size="small" />;
      break;

    default:
      icon = <FivePeople size="small" />;
  }
  return (
    <RadioTile key={value} value={value} icon={icon} subtitle={description || undefined}>
      {label}
    </RadioTile>
  );
};

export const ResidentsStep: FC = () => {
  const { ResidentsStep: { fields: textualData } = {} } = usePlaceholderContent<{
    ResidentsStep: ResidentsStepRendering;
  }>();

  const {
    form: {
      handleSubmit,
      control,
      formState: { errors, isSubmitSuccessful },
    },
  } = useFlowStep<FormFields>({
    buildSchema: () =>
      yup.object({
        residents: requiredRadioChoice(textualData?.choice?.residentsRadio?.value?.requiredMessage),
      }),
    defaultValues: context => ({
      residents: context.residents == null ? undefined : String(context.residents),
    }),
    onSubmitSuccess: formData => ({
      residents: Number(formData.residents) || undefined,
    }),
  });

  return (
    <StepShell
      handleSubmit={handleSubmit}
      textualData={textualData}
      isLoading={isSubmitSuccessful}
      nextButtonLabel={textualData?.content?.nextStepText?.value}>
      <FlowRadioGroup
        control={control}
        name="residents"
        {...getRadioGroupNameProps(textualData?.choice?.residentsRadio?.value?.label)}
        hint={textualData?.choice?.residentsRadio?.value?.hint || undefined}
        direction="column"
        error={errors['residents']?.message}>
        {textualData?.choice?.residentsRadio?.value?.options?.map(option =>
          generateInputElement(option.name, option.label, option.description),
        )}
      </FlowRadioGroup>
    </StepShell>
  );
};
